import React, { useState, useEffect, useRef } from 'react';
import queryString from 'query-string';
import { Form, Progress, Alert } from 'reactstrap';
import { CSSTransition } from 'react-transition-group';

import { useLocalization } from '../../containers/I18nProvider';
import { WithCaptcha } from '../../containers/captcha';

import ecuanetLogo from './img/logo.png';

const LoginForm = ({ api, location, TurnstileComponent, ...props }) => {
  const nodeRef = useRef(null);
  const { translate, changeDefaultLocale, changeLocalePath } = useLocalization();

  useEffect(() => {
    changeDefaultLocale('es');
    changeLocalePath('/lang/ecuanet');
  }, [changeDefaultLocale, changeLocalePath]);

  const [onLogin, setOnLogin] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState({
    open: false,
    message: '',
  });
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const parameters = queryString.parse(location.search);

  useEffect(() => {
    setShowForm(true);
  }, []);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setOnLogin(true);
    setError({
      open: false,
      message: '',
    });

    api
      .userPassLogin(
        {
          username,
          password,
        },
        parameters
      )
      .then((response) => {
        setOnLogin(false);
        window.location =
          parameters.redirect_uri + '?' + queryString.stringify(response);
      })
      .catch((apiError) => {
        const translatedError = translate(apiError.errorCode);
        let message = translate('RequestLoginErrorMessage');

        if (translatedError) {
          message = translatedError;
        } else if (apiError.details) {
          message = apiError.details;
        }

        setOnLogin(false);
        setError({ open: true, message });
      });
  };

  return (
    <div className="ecuanet-login-wrapper">
      <CSSTransition
        in={showForm}
        timeout={300}
        classNames="ecuanet-formshow"
        nodeRef={nodeRef}
        unmountOnExit
      >
        <div className="ecuanet-login-content" ref={nodeRef}>
          <img src={ecuanetLogo} alt="Ecuanet" className="ecuanet-logo" />

          <h1 className="ecuanet-title">{translate('Title')}</h1>

          <Form onSubmit={(event) => onSubmit(event)} className="ecuanet-form">
            <div className="ecuanet-input-group">
              <span className="ecuanet-icon-wrap" aria-hidden="true">
                <i className="fa fa-user" />
              </span>
              <input
                type="text"
                className="ecuanet-input"
                disabled={onLogin}
                required={true}
                tabIndex={1}
                autoCorrect="off"
                autoCapitalize="none"
                id="username"
                placeholder={translate('PlaceholderUserName')}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>

            <div className="ecuanet-input-group">
              <span className="ecuanet-icon-wrap" aria-hidden="true">
                <i className="fa fa-lock" />
              </span>
              <input
                type={showPassword ? 'text' : 'password'}
                className="ecuanet-input"
                required={true}
                disabled={onLogin}
                tabIndex={2}
                autoCorrect="off"
                autoCapitalize="none"
                id="password"
                placeholder={translate('PlaceholderPassword')}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                className="ecuanet-password-btn"
                type="button"
                onClick={togglePasswordVisibility}
                aria-label={translate('TogglePassword')}
              >
                <i
                  className={showPassword ? 'fa fa-eye-slash' : 'fa fa-eye'}
                  aria-hidden="true"
                />
              </button>
            </div>

            <div className="ecuanet-actions">
              {!onLogin ? (
                <button
                  tabIndex={3}
                  type="submit"
                  className="ecuanet-login-btn"
                >
                  {translate('ButtonEnter')}
                </button>
              ) : (
                <Progress animated color="danger" value={100} className="ecuanet-progress" />
              )}
            </div>

            <div className="messages-container">
              <Alert
                color="danger"
                id="error-message"
                isOpen={error.open}
                toggle={() => setError({ open: false })}
              >
                {error.message}
              </Alert>
            </div>

            <TurnstileComponent setTurnstileSuccess={props.setTurnstileSuccess} />
          </Form>

          <div className="ecuanet-support-text">
            <span className="ecuanet-support-label">{translate('SupportText')}</span>
            <a className="ecuanet-phone-link" href={`tel:${translate('SupportPhoneRaw')}`}>
              {translate('SupportPhone')}
            </a>
          </div>

          <div className="ecuanet-footer-links">
            <a
              href="https://ecuanet.ec/politica-tratamiento-datos-personales/"
              target="_blank"
              rel="noopener noreferrer"
            >
              {translate('PrivacyPolicy')}
            </a>
          </div>

          <div className="ecuanet-footer">{translate('Footer')}</div>
        </div>
      </CSSTransition>
    </div>
  );
};

export default WithCaptcha(LoginForm, { useInsideForm: true, style: { marginTop: '1rem' } });
